/**
 * THE AUTHORITY TO PAY — the page that goes to finance (Deliverable 2).
 *
 * WHAT THIS IS. When a budget line is approved for payment, FlightDeck mints a
 * one-page "Authority to pay": the facts of the approval — who authorised it, on
 * what basis, for how much, against which purchase order — drawn as a document
 * finance can file and act on. It is the human face of the approval.
 *
 * THE STAMP GOES ON A COPY OF THE INVOICE — Vinnie, 3 October 2026. "View
 * authority to pay" now opens the ACTUAL uploaded invoice with the authorisation
 * stamp drawn on its first page (see `stampInvoiceToPay`). The audit concern that
 * first argued against this is met by stamping a COPY: the stored original is
 * never touched — its bytes, and the fingerprint the approval was taken against,
 * stay exactly as uploaded — so an auditor still has the pristine document, and
 * the fingerprint check still works. A scan or photo (PNG/JPG) is placed onto a
 * page and stamped the same way. The standalone page below (`authorityToPayPdf`)
 * is kept as the FALLBACK for a file type that cannot be stamped (anything that is
 * not a PDF or a PNG/JPG image); it is BOUND to the invoice by citing the file's
 * fingerprint rather than being written on top of it.
 *
 * (Superseded 2 October note: the authority was first chosen as a separate page
 * precisely so as not to write over the supplier's original — the copy approach
 * keeps that guarantee while giving Vinnie the stamped invoice he expects.)
 *
 * WHAT "BOUND TO THE INVOICE" MEANS, precisely. The approval is taken against a
 * specific document: at approval time the line records the SHA-256 of the invoice
 * file (the same fingerprint mechanism a signed document uses — see
 * documents.ts). This page prints that fingerprint. If the invoice is ever
 * replaced, its bytes hash differently, the recorded fingerprint no longer
 * matches, and the approval is void (see payStampState in projects.ts) — the line
 * drops back to be approved again. So the fingerprint on this page is not
 * decoration: it is what makes the authority checkable, and what stops an
 * approval given for one invoice being presented against another.
 *
 * TAKES WHAT IT PRINTS. Like the change-request PDF, this lays out facts it is
 * handed and reaches for nothing — no board read, no clock — so the same approval
 * always produces the same bytes and the layout can be tested without a database.
 *
 * PLAIN HELVETICA, NO EMBEDDED FONT — one of the fourteen faces every reader
 * already has, so a document kept for years opens the same everywhere.
 */
import { PDFDocument, StandardFonts, rgb, type PDFFont, type PDFPage } from 'pdf-lib';
import { winAnsi, unwrap } from './pdf-text';
import {
  drawLetterhead,
  drawBand,
  wordedFromSlashed,
  TINT_LABEL,
  BRAND,
  type Letterhead,
} from './pdf-chrome';
import { embedTitleFont } from './pdf-fonts';

/** A4 in points, which is what pdf-lib measures in. */
const PAGE = { width: 595.28, height: 841.89 };
const MARGIN = 52;
const WIDTH = PAGE.width - MARGIN * 2;
const FOOT = 38;

const INK = rgb(0.1, 0.13, 0.16);
const QUIET = rgb(0.42, 0.46, 0.5);
const RULE = rgb(0.8, 0.83, 0.85);
const SUNK = rgb(0.965, 0.972, 0.976);
/** An exception is called out in a warm amber — the same warning register the app
 *  uses for a control exception, legible when printed in black and white too. */
const WARN = rgb(0.7, 0.42, 0.04);
const WARN_SUNK = rgb(0.996, 0.957, 0.886);

/** The customer's mark, passed as bytes so this file draws only what it is given. */
export interface Logo {
  bytes: ArrayBuffer | Uint8Array;
  type: 'png' | 'jpg';
}

/**
 * EVERYTHING THE PAGE SAYS, worked out by the caller. How the approval was given
 * (`basis`) picks the wording; `coverFor` and `exceptionReason` fill the two
 * special cases. Amounts and dates arrive pre-formatted so the currency and the
 * day-first date are decided in one place (the store), not here.
 */
export interface AuthorityToPayFacts {
  /** The board the line belongs to. */
  board: string;
  /** The stamp reference, SA-xxxx (payStampRef). The document's own number. */
  reference: string;
  /** Who the payment is to. */
  supplier: string;
  /** The amount, already formatted with its currency symbol — e.g. "TT$12,500". */
  amount: string;
  /** The purchase-order number, if the line had one. */
  poNumber?: string;
  /** The invoice reference, if set. */
  invoiceRef?: string;
  /** One line describing what the spend is for. */
  description?: string;
  /** The classification line — e.g. "Capital · Rollout" or "Operating". */
  classification: string;
  /** How the approval was given, which chooses the wording. */
  basis: 'approver' | 'cover' | 'exception';
  /** The person whose authority this is — the approver, or the person a cover
   *  stood in for. Shown as the authorising name. */
  approver: string;
  /** The person who actually approved, when that differs from `approver`
   *  (delegated cover, or an admin standing in). */
  approvedBy: string;
  /** The reason recorded on a control exception (admin stood in, or a
   *  separation-of-duties override). Its presence makes the exception band show. */
  exceptionReason?: string;
  /** When it was approved, day-first — e.g. "02/10/2026". Dates the file too. */
  approvedOn: string;
  /** The clock time it was approved, to the minute — e.g. "14:37". */
  approvedTime: string;
  /** The supplier invoice this authority is bound to. */
  invoiceName: string;
  /** The invoice file's SHA-256, hex — what the authority is bound to. Empty on an
   *  approval taken before fingerprinting existed, which the page says plainly. */
  invoiceHash: string;
  /** The app version, for the foot of the page. */
  version: string;
  logo?: Logo;
}

/** Day-first, which is what the Caribbean and the UK read. */
export const asDate = (at: number): string => {
  const d = new Date(at);
  const two = (n: number) => String(n).padStart(2, '0');
  return `${two(d.getDate())}/${two(d.getMonth() + 1)}/${d.getFullYear()}`;
};

/** The clock time, to the minute. */
export const asTime = (at: number): string => {
  const d = new Date(at);
  const two = (n: number) => String(n).padStart(2, '0');
  return `${two(d.getHours())}:${two(d.getMinutes())}`;
};

/** Words that fit, measured in the font they will be drawn in. */
function wrap(text: string, font: PDFFont, size: number, measure: number): string[] {
  const lines: string[] = [];
  for (const paragraph of unwrap(winAnsi(text)).split('\n')) {
    let line = '';
    for (const word of paragraph.split(/\s+/).filter(Boolean)) {
      const tried = line ? `${line} ${word}` : word;
      if (font.widthOfTextAtSize(tried, size) <= measure) line = tried;
      else {
        if (line) lines.push(line);
        line = word;
      }
    }
    lines.push(line);
  }
  return lines;
}

/** The same approval produces the same bytes — dated from the approval, never the
 *  clock, so the file is a function of its content. (Same reason as the change
 *  request, which is signed against a fingerprint of itself.) */
function stampDates(pdf: PDFDocument, approvedOn: string): void {
  const [day, month, year] = approvedOn.split('/').map(Number);
  const when =
    Number.isFinite(day) && Number.isFinite(month) && Number.isFinite(year)
      ? new Date(Date.UTC(year, month - 1, day))
      : new Date(Date.UTC(2000, 0, 1));
  pdf.setCreationDate(when);
  pdf.setModificationDate(when);
}

type Art = Parameters<PDFPage['drawImage']>[0] & { width: number; height: number };

async function embed(pdf: PDFDocument, logo?: Logo): Promise<Art | undefined> {
  if (!logo) return undefined;
  try {
    return logo.type === 'png' ? await pdf.embedPng(logo.bytes) : await pdf.embedJpg(logo.bytes);
  } catch {
    return undefined; // The document is the point; losing the mark is only a blemish.
  }
}

/** A pen that knows where it is on the page and starts a new one when it runs out. */
function makePen(pdf: PDFDocument, regular: PDFFont, bold: PDFFont, titleFont?: PDFFont) {
  let page: PDFPage = pdf.addPage([PAGE.width, PAGE.height]);
  let y = PAGE.height - MARGIN;

  const room = (needed: number) => {
    if (y - needed >= MARGIN + FOOT) return;
    page = pdf.addPage([PAGE.width, PAGE.height]);
    y = PAGE.height - MARGIN;
  };

  return {
    get pages() {
      return pdf.getPageCount();
    },
    down: (by: number) => {
      y -= by;
    },
    room,

    image(width: number, height: number, art: Parameters<PDFPage['drawImage']>[0]) {
      room(height + 10);
      y -= height;
      page.drawImage(art, { x: MARGIN, y, width, height });
      y -= 10;
    },

    text(
      body: string,
      opts: { size?: number; bold?: boolean; colour?: typeof INK; indent?: number; gap?: number } = {},
    ) {
      const size = opts.size ?? 10;
      const font = opts.bold ? bold : regular;
      const indent = opts.indent ?? 0;
      const leading = size * 1.5;
      for (const line of wrap(body, font, size, WIDTH - indent)) {
        room(leading);
        y -= leading;
        page.drawText(line, { x: MARGIN + indent, y, size, font, color: opts.colour ?? INK });
      }
      if (opts.gap) y -= opts.gap;
    },

    /** A tinted section band (R18), numbered like the mockup. */
    band(title: string, number?: number) {
      const height = 26;
      room(height + 30);
      y -= height + 8;
      drawBand(page, { x: MARGIN, yBottom: y, width: WIDTH, title: winAnsi(title), number, bold });
      y -= 10;
    },

    /** The letterhead (R18): eyebrow, big subject, quiet meta, logo, teal hairline. */
    letterhead(content: Letterhead) {
      y = drawLetterhead(
        page,
        { x: MARGIN, width: WIDTH, top: PAGE.height - MARGIN },
        { regular, bold, title: titleFont },
        content,
      );
    },

    /** A bordered table; label cells take the band colour, as the change request's do. */
    table(widths: number[], rows: string[][], opts: { labels?: number[] } = {}) {
      const size = 9.5;
      const pad = 6;
      const leading = size * 1.35;
      const cols = widths.map((w) => w * WIDTH);
      rows.forEach((row) => {
        const isLabel = (i: number) => !!opts.labels?.includes(i);
        const wrapped = row.map((cell, i) =>
          wrap(cell, isLabel(i) ? bold : regular, size, cols[i] - pad * 2),
        );
        const height = Math.max(...wrapped.map((w) => w.length)) * leading + pad * 2 - 2;
        room(height);
        y -= height;
        let x = MARGIN;
        wrapped.forEach((cell, i) => {
          if (isLabel(i)) page.drawRectangle({ x, y, width: cols[i], height, color: TINT_LABEL });
          page.drawRectangle({ x, y, width: cols[i], height, borderColor: RULE, borderWidth: 0.75 });
          cell.forEach((line, n) => {
            page.drawText(line, {
              x: x + pad,
              y: y + height - pad - size + 1 - n * leading,
              size,
              font: isLabel(i) ? bold : regular,
              color: isLabel(i) ? BRAND : INK,
            });
          });
          x += cols[i];
        });
      });
    },

    /** A sunk panel the fingerprint sits in, so it reads as the document's seal.
     *  A value is free-form — an exception reason is a sentence, an invoice name can
     *  run long — so each value WRAPS within the panel and the panel grows to fit,
     *  rather than running off the box and the page edge (it is a signed finance
     *  document; nothing on it may fall off the paper). */
    seal(label: string, lines: { k: string; v: string }[], tone: 'quiet' | 'warn' = 'quiet') {
      const pad = 11;
      const labelH = 16;
      const size = 8.5;
      const lead = 12;
      const valX = MARGIN + pad + 120;
      const valW = WIDTH - 120 - pad * 2;
      const rows = lines.map((ln) => ({ k: ln.k, vs: wrap(ln.v, bold, size, valW) }));
      const rowH = (r: { vs: string[] }) => Math.max(1, r.vs.length) * lead + 3;
      const height = labelH + rows.reduce((h, r) => h + rowH(r), 0) + pad * 2 - 4;
      room(height + 12);
      y -= height + 10;
      const bg = tone === 'warn' ? WARN_SUNK : SUNK;
      const edge = tone === 'warn' ? WARN : RULE;
      page.drawRectangle({ x: MARGIN, y, width: WIDTH, height, color: bg, borderColor: edge, borderWidth: 0.75 });
      page.drawText(winAnsi(label), {
        x: MARGIN + pad,
        y: y + height - pad - 9,
        size: 8,
        font: bold,
        color: tone === 'warn' ? WARN : QUIET,
      });
      let ry = y + height - pad - labelH - 8;
      rows.forEach((r) => {
        page.drawText(winAnsi(r.k), { x: MARGIN + pad, y: ry, size, font: regular, color: QUIET });
        r.vs.forEach((vl, i) =>
          page.drawText(vl, { x: valX, y: ry - i * lead, size, font: bold, color: INK }),
        );
        ry -= rowH(r);
      });
    },
  };
}

/** The foot of every page: what it is, which board, and page n of m. */
function stampFooters(pdf: PDFDocument, facts: AuthorityToPayFacts, font: PDFFont): void {
  const pages = pdf.getPages();
  pages.forEach((page, at) => {
    const size = 7.5;
    page.drawLine({
      start: { x: MARGIN, y: MARGIN + 22 },
      end: { x: MARGIN + WIDTH, y: MARGIN + 22 },
      thickness: 0.75,
      color: RULE,
    });
    const said = winAnsi(`Authority to pay ${facts.reference} — ${facts.board} · FlightDeck ${facts.version}`);
    page.drawText(said, { x: MARGIN, y: MARGIN + 10, size, font, color: QUIET });
    const numbered = winAnsi(`page ${at + 1} of ${pages.length}`);
    page.drawText(numbered, {
      x: MARGIN + WIDTH - font.widthOfTextAtSize(numbered, size),
      y: MARGIN + 10,
      size,
      font,
      color: QUIET,
    });
  });
}

/** The sentence that says who authorised the payment, chosen by how it was given. */
function authoritySentence(f: AuthorityToPayFacts): string {
  if (f.basis === 'cover') {
    return `This payment was authorised by ${f.approvedBy}, standing in as delegated cover for ${f.approver}, on ${wordedFromSlashed(f.approvedOn)} at ${f.approvedTime}.`;
  }
  if (f.basis === 'exception') {
    return `This payment was authorised by ${f.approvedBy} in the approver’s place on ${wordedFromSlashed(f.approvedOn)} at ${f.approvedTime}. It is recorded as a control exception.`;
  }
  return `This payment was authorised by ${f.approver} on ${wordedFromSlashed(f.approvedOn)} at ${f.approvedTime}.`;
}

/**
 * The authority to pay, as bytes.
 */
export async function authorityToPayPdf(facts: AuthorityToPayFacts): Promise<Uint8Array> {
  const pdf = await PDFDocument.create();
  const regular = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);

  pdf.setTitle(`Authority to pay ${facts.reference}`);
  pdf.setSubject(`Authority to pay — ${facts.supplier}, ${facts.board}`);
  pdf.setProducer('FlightDeck');
  stampDates(pdf, facts.approvedOn);

  const art = await embed(pdf, facts.logo);
  const titleFont = await embedTitleFont(pdf);
  const pen = makePen(pdf, regular, bold, titleFont);
  // The authority names who stands behind it — the approver — right before the
  // date and time it was approved, in the document's own verb (parallel to the
  // "Prepared by" the other documents carry). The seal below still sets out the
  // approver / cover / exception in full; this is the one-line by-line.
  const approvedWhen = `${wordedFromSlashed(facts.approvedOn)}, ${facts.approvedTime}`;
  const approvedWho = (facts.approvedBy || facts.approver || '').trim();
  pen.letterhead({
    eyebrow: 'Authority to pay',
    subject: facts.reference,
    meta: [
      facts.board,
      ...(approvedWho ? [`Approved by ${approvedWho}`, approvedWhen] : [`Approved ${approvedWhen}`]),
    ]
      .filter(Boolean)
      .join('  ·  '),
    logo: art,
  });

  /* ── 1. Payment ───────────────────────────────────────────────────────── */
  pen.band('Payment', 1);
  pen.table(
    [0.2, 0.3, 0.2, 0.3],
    [
      ['Supplier', facts.supplier || '—', 'Amount', facts.amount || '—'],
      ['PO number', facts.poNumber || '—', 'Invoice ref', facts.invoiceRef || '—'],
      ['Classification', facts.classification || '—', 'Project', facts.board || '—'],
    ],
    { labels: [0, 2] },
  );
  if ((facts.description ?? '').trim()) {
    pen.down(4);
    pen.text('For', { size: 9, bold: true, colour: QUIET });
    pen.text(facts.description!.trim(), { size: 9.5 });
  }
  pen.down(4);

  /* ── 2. Authority ─────────────────────────────────────────────────────── */
  const exception = facts.basis === 'exception' && !!(facts.exceptionReason ?? '').trim();
  pen.band('Authority', 2);
  pen.text(authoritySentence(facts), { size: 10, gap: 4 });
  pen.table(
    [0.26, 0.74],
    [
      ['Authorising officer', facts.approver || '—'],
      ['Approved by', facts.approvedBy || facts.approver || '—'],
      [
        'Basis',
        facts.basis === 'cover'
          ? 'Delegated cover'
          : facts.basis === 'exception'
            ? 'Control exception'
            : 'Designated approver',
      ],
      ['Date', `${wordedFromSlashed(facts.approvedOn)} at ${facts.approvedTime}`],
    ],
    { labels: [0] },
  );
  if (exception) {
    pen.seal(
      'CONTROL EXCEPTION — REASON RECORDED',
      [{ k: 'Reason', v: facts.exceptionReason!.trim() }],
      'warn',
    );
  }
  pen.down(4);

  /* ── 3. Bound to this invoice ─────────────────────────────────────────── */
  pen.band('Bound to this invoice', 3);
  if (facts.invoiceHash) {
    pen.text(
      'This authority is bound to the supplier invoice below. If the invoice file is replaced, ' +
        'its fingerprint changes, this authority no longer matches it, and the line must be ' +
        'approved again. Check the fingerprint against the stored invoice to confirm this ' +
        'authority was given for the document you are paying.',
      { size: 9.5, colour: QUIET, gap: 2 },
    );
    pen.seal('BOUND DOCUMENT', [
      { k: 'Invoice file', v: facts.invoiceName || '—' },
      { k: 'Fingerprint', v: facts.invoiceHash },
    ]);
  } else {
    pen.text(
      'This authority predates document fingerprinting, so it is not cryptographically bound to ' +
        'the invoice file. Re-approving the line against the current invoice will bind it.',
      { size: 9.5, colour: QUIET },
    );
    pen.seal('BOUND DOCUMENT', [{ k: 'Invoice file', v: facts.invoiceName || '—' }]);
  }

  stampFooters(pdf, facts, regular);
  return pdf.save();
}

/* ════════════════════════════════════════════════════════════════════════════
   THE STAMP ON A COPY OF THE INVOICE (Vinnie, 3 Oct 2026).

   Draw the authorisation as a stamp over page 1 of the supplier's own invoice and
   hand that back, so "View authority to pay" shows the invoice a finance officer
   would recognise — marked, like a rubber stamp. We work on a COPY: the caller
   fetches the stored bytes and passes them in, we load/redraw in memory and return
   new bytes; the file in storage is never written, so the original and its
   fingerprint are untouched. A PDF is stamped on its first page; a PNG/JPG scan is
   laid onto an A4 page and stamped the same way. Any other type returns null and
   the caller falls back to the standalone authority page above.
   ════════════════════════════════════════════════════════════════════════════ */

/** The authorised green — clear in colour and still legible in black and white. */
const STAMP_OK = rgb(0.09, 0.45, 0.33);

/** What the stamp says — the same facts as the page, trimmed to a stamp's worth. */
export interface InvoiceStampFacts {
  /** The stamp reference, SA-xxxx. */
  reference: string;
  /** How the approval was given — picks the wording and the colour. */
  basis: 'approver' | 'cover' | 'exception';
  /** Whose authority this is — the approver, or the person a cover stood in for. */
  approver: string;
  /** Who actually approved (may differ from `approver` on cover / exception). */
  approvedBy: string;
  /** When, day-first — e.g. "02/10/2026". */
  approvedOn: string;
  /** The clock time, to the minute — e.g. "14:37". */
  approvedTime: string;
  /** The amount, already formatted with its currency symbol. */
  amount: string;
  /** The invoice file's SHA-256 (hex) — shown short, to tie stamp to document. */
  invoiceHash: string;
}

/**
 * Stamp a COPY of the supplier invoice and return the stamped bytes, or null if the
 * file is a type we cannot stamp (caller falls back to the standalone page).
 * `invoiceBytes` are the stored file's bytes; `fileName` gives the type by extension.
 */
export async function stampInvoiceToPay(
  invoiceBytes: ArrayBuffer | Uint8Array,
  fileName: string,
  f: InvoiceStampFacts,
): Promise<Uint8Array | null> {
  const ext = (fileName.split('.').pop() ?? '').toLowerCase();
  let pdf: PDFDocument;
  let page: PDFPage;
  try {
    if (ext === 'pdf') {
      pdf = await PDFDocument.load(invoiceBytes, { ignoreEncryption: true });
      if (pdf.getPageCount() === 0) return null;
      page = pdf.getPage(0);
    } else if (ext === 'png' || ext === 'jpg' || ext === 'jpeg') {
      pdf = await PDFDocument.create();
      const img = ext === 'png' ? await pdf.embedPng(invoiceBytes) : await pdf.embedJpg(invoiceBytes);
      const maxW = PAGE.width - 36 * 2;
      const maxH = PAGE.height - 36 * 2;
      const scale = Math.min(maxW / img.width, maxH / img.height, 1);
      const w = img.width * scale;
      const h = img.height * scale;
      page = pdf.addPage([PAGE.width, PAGE.height]);
      page.drawImage(img, { x: (PAGE.width - w) / 2, y: (PAGE.height - h) / 2, width: w, height: h });
    } else {
      return null;
    }
  } catch {
    return null; // Unreadable / unsupported — the caller shows the page instead.
  }

  const regular = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  stampDates(pdf, f.approvedOn);

  const exception = f.basis === 'exception';
  const edge = exception ? WARN : STAMP_OK;
  const { width: pw, height: ph } = page.getSize();
  const boxW = Math.min(252, pw - 28);
  const pad = 11;
  const inner = boxW - pad * 2;
  const gap = 4;

  const who =
    f.basis === 'cover'
      ? `${f.approvedBy} for ${f.approver} — delegated cover`
      : f.basis === 'exception'
        ? `${f.approvedBy} — in the approver’s place`
        : `${f.approvedBy || f.approver} — designated approver`;
  const bound = f.invoiceHash
    ? `Bound ${f.invoiceHash.slice(0, 8)}…${f.invoiceHash.slice(-4)}`
    : 'Bound to this invoice';

  type Seg = { t: string; size: number; font: PDFFont; color: typeof INK };
  const seg: Seg[] = [{ t: 'AUTHORISED TO PAY', size: 11, font: bold, color: edge }];
  if (exception) seg.push({ t: 'Control exception', size: 8.5, font: bold, color: WARN });
  seg.push({ t: f.reference, size: 9.5, font: bold, color: INK });
  for (const w of wrap(who, regular, 8.5, inner)) seg.push({ t: w, size: 8.5, font: regular, color: QUIET });
  seg.push({ t: `${f.approvedOn} at ${f.approvedTime}`, size: 8.5, font: regular, color: QUIET });
  seg.push({ t: bound, size: 8.5, font: regular, color: QUIET });
  if (f.amount) seg.push({ t: `Amount ${f.amount}`, size: 9, font: bold, color: INK });

  const boxH = seg.reduce((h, s) => h + s.size + gap, 0) - gap + pad * 2;
  const x = pw - boxW - 20;
  const y = ph - boxH - 20;

  // A white backing at high opacity so the stamp reads over any invoice content,
  // with the tone's border — a stamp wears a border by design (not a UI badge).
  page.drawRectangle({ x, y, width: boxW, height: boxH, color: rgb(1, 1, 1), opacity: 0.92 });
  page.drawRectangle({ x, y, width: boxW, height: boxH, borderColor: edge, borderWidth: 1.5 });

  let cy = y + boxH - pad;
  for (const s of seg) {
    cy -= s.size;
    page.drawText(winAnsi(s.t), { x: x + pad, y: cy, size: s.size, font: s.font, color: s.color });
    cy -= gap;
  }

  return pdf.save();
}
